import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import moment from 'moment';
import { DeliveryPlanViewerOrder, DeliveryPlanViewerUnassignedOrder } from '../delivery-plan-viewer.models';
import {
    DATE_FORMAT,
    DATE_PICKER_CONFIG,
    DATE_PICKER_CONTAINER,
    DATE_PICKER_MIN_DATE,
    DATE_PICKER_MAX_DATE,
    TIME_FORMAT,
    TOAST_COPY_DURATION_MS,
} from '../delivery-plan-viewer.constants';
import { copyToClipboard, InlineToastService } from 'shared';

@Component({
    selector: 'app-delivery-plan-viewer-left-panel',
    templateUrl: './delivery-plan-viewer-left-panel.component.html',
    styleUrls: ['./delivery-plan-viewer-left-panel.component.scss'],
})
export class DeliveryPlanViewerLeftPanelComponent implements OnChanges
{
    @Input() public orders: DeliveryPlanViewerOrder[] = [];
    @Input() public unassigned: DeliveryPlanViewerUnassignedOrder[] = [];
    @Input() public showUnassignedOnMap = true;
    @Input() public canToggleUnassignedOnMap = false;
    @Input() public deliveryDate: Date = new Date();
    @Input() public isLoadingPlan = false;
    @Input() public selectedOrderCode: string | null = null;

    @Output() public changeDate = new EventEmitter<number>();
    @Output() public pickDate = new EventEmitter<Date>();
    @Output() public deleteOrder = new EventEmitter<string>();
    @Output() public selectOrder = new EventEmitter<DeliveryPlanViewerOrder | null>();
    @Output() public selectUnassignedOrder = new EventEmitter<DeliveryPlanViewerUnassignedOrder | null>();
    @Output() public toggleUnassignedOnMap = new EventEmitter<void>();

    public readonly minDate = DATE_PICKER_MIN_DATE;
    public readonly maxDate = DATE_PICKER_MAX_DATE;

    public readonly datepickerConfig = DATE_PICKER_CONFIG;

    /**
     * Element the date picker popup is attached to: this panel (its own selector). The default
     * ('body') puts the popup outside the fullscreen modal, where it ends up beneath it.
     */
    public readonly datepickerContainer = DATE_PICKER_CONTAINER;

    public searchQuery = "";

    public showUnassigned = true;

    public sortedOrders: DeliveryPlanViewerOrder[] = [];
    public filteredUnassigned: DeliveryPlanViewerUnassignedOrder[] = [];

    constructor(private toast: InlineToastService) {}

    public get normalizedQuery(): string
    {
        return this.searchQuery.trim().toLowerCase();
    }

    public ngOnChanges(): void
    {
        this.applySearch();
    }

    public formatDate(date: Date): string
    {
        return moment(date).format(DATE_FORMAT);
    }

    public formatTime(date: Date | null): string
    {
        return date ? moment(date).format(TIME_FORMAT) : '';
    }

    public onSearchChanged(query: string): void
    {
        this.searchQuery = query;
        this.applySearch();
    }

    public onDatePicked(date: Date | undefined): void
    {
        if (date && !moment(date).isSame(this.deliveryDate, 'day'))
            this.pickDate.emit(date);
    }

    public onOrderClicked(order: DeliveryPlanViewerOrder): void
    {
        this.selectOrder.emit(this.selectedOrderCode === order.code ? null : order);
    }

    public hasLocation(order: DeliveryPlanViewerUnassignedOrder): boolean
    {
        return order.lat != null && order.lng != null;
    }

    public onUnassignedClicked(order: DeliveryPlanViewerUnassignedOrder): void
    {
        if (!this.hasLocation(order))
            return;

         this.selectUnassignedOrder.emit(this.selectedOrderCode === order.code ? null : order);
    }

    public onToggleUnassignedOnMapClicked(event: MouseEvent): void
    {
        event.stopPropagation();
        this.toggleUnassignedOnMap.emit();
    }

    public onDeleteClicked(event: MouseEvent, code: string): void
    {
        event.stopPropagation();
        this.deleteOrder.emit(code);
    }

    public async onCopyClicked(event: MouseEvent, code: string): Promise<void>
    {
        event.stopPropagation();
        const copied = await copyToClipboard(code);

        this.toast.show(
            copied ? "delivery-plan-viewer.copied" : "delivery-plan-viewer.copy-blocked",
            { code },
            TOAST_COPY_DURATION_MS
        );
    }

    /** Keeps the order / unassigned cards' DOM when the list changes (selection, search). */
    public trackByCode(_index: number, order: DeliveryPlanViewerOrder | DeliveryPlanViewerUnassignedOrder): string
    {
        return order.code;
    }

    /**
     * Rebuilds both lists from the inputs and the search: order codes containing the search text
     * (case-insensitive) are kept, the orders are sorted by pickup ETA, earliest first, and
     * orders without a pickup ETA go to the end.
     */
    private applySearch(): void
    {
        const query = this.normalizedQuery;

        const filtered = query
            ? this.orders.filter((order) => order.code.toLowerCase().includes(query))
            : this.orders;
        this.sortedOrders = [...filtered].sort((a, b) =>
        {
            if (!a.pickupEta && !b.pickupEta)
                return 0;
            if (!a.pickupEta)
                return 1;
            if (!b.pickupEta)
                return -1;
            return a.pickupEta.getTime() - b.pickupEta.getTime();
        });

        this.filteredUnassigned = query
            ? this.unassigned.filter((order) => order.code.toLowerCase().includes(query))
            : this.unassigned;
    }

}